/**
 * PushToggle — komponent UI do włączania/wyłączania Web Push notifications.
 *
 * Stany:
 *  - unsupported: przeglądarka nie obsługuje (np. iOS Safari < 16.4) → ukryty
 *  - default: pokazuje "Włącz powiadomienia" (CTA)
 *  - granted+subscribed: pokazuje "✓ Powiadomienia ON" + button wyłącz
 *  - denied: pokazuje info "Zablokowane w przeglądarce — odblokuj w ustawieniach"
 *
 * Po kliknięciu "Włącz" → request permission → subscribe → POST /api/push/subscribe.
 */
import { useEffect, useState } from 'react';
import { Bell, BellOff, BellRing, Loader2, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  isPushSupported, getPushState, enablePush, disablePush, sendTestPush,
} from '@/utils/pushNotifications';

export default function PushToggle({ variant = 'card' }) {
  const [state, setState] = useState({ supported: false, permission: 'default', subscribed: false });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    if (!isPushSupported()) return;
    getPushState().then((s) => { if (alive) setState(s); });
    return () => { alive = false; };
  }, []);

  if (!state.supported) {
    return null; // ukryj na nieobsługiwanych (iOS Safari < 16.4, etc.)
  }

  const handleEnable = async () => {
    setBusy(true);
    try {
      await enablePush();
      setState({ supported: true, permission: 'granted', subscribed: true });
      toast.success('🔔 Powiadomienia włączone', {
        description: 'Damy znać o nowych szczeniakach w obserwowanych hodowlach.',
      });
    } catch (e) {
      const code = e?.message || 'ERROR';
      if (code === 'DENIED') {
        toast.error('Powiadomienia zablokowane', {
          description: 'Odblokuj w ustawieniach przeglądarki (kłódka obok adresu).',
        });
      } else if (code === 'DISMISSED') {
        toast('Anulowano', { description: 'Możesz włączyć w każdej chwili.' });
      } else if (code === 'VAPID_NOT_CONFIGURED') {
        toast.error('Powiadomienia tymczasowo niedostępne');
      } else {
        toast.error('Nie udało się włączyć', { description: 'Spróbuj ponownie za chwilę.' });
      }
      // Odśwież stan
      const s = await getPushState();
      setState(s);
    } finally {
      setBusy(false);
    }
  };

  const handleDisable = async () => {
    setBusy(true);
    try {
      await disablePush();
      setState((s) => ({ ...s, subscribed: false }));
      toast('🔕 Powiadomienia wyłączone');
    } catch {
      toast.error('Nie udało się wyłączyć');
    } finally {
      setBusy(false);
    }
  };

  const handleTest = async () => {
    try {
      await sendTestPush();
      toast.success('Wysłano testowe powiadomienie', {
        description: 'Sprawdź pasek powiadomień systemu.',
      });
    } catch {
      toast.error('Nie udało się wysłać testu');
    }
  };

  // ============== VARIANT: card (default — pełna sekcja w /moje/obserwowane) ==============
  if (variant === 'card') {
    if (state.permission === 'denied') {
      return (
        <div
          className="rounded-xl bg-amber-500/5 border border-amber-500/20 p-4 flex items-start gap-3"
          data-testid="push-toggle-denied"
        >
          <BellOff className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-amber-200">Powiadomienia zablokowane</p>
            <p className="text-xs text-gray-400 mt-1">
              Odblokuj w ustawieniach przeglądarki (kliknij kłódkę obok adresu strony → „Powiadomienia: Zezwalaj").
            </p>
          </div>
        </div>
      );
    }

    if (state.subscribed) {
      return (
        <div
          className="rounded-xl bg-emerald-500/5 border border-emerald-500/20 p-4 flex items-center gap-3"
          data-testid="push-toggle-on"
        >
          <div className="w-9 h-9 rounded-full bg-emerald-500/15 flex items-center justify-center shrink-0">
            <Check className="w-4 h-4 text-emerald-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-emerald-200">Powiadomienia włączone</p>
            <p className="text-xs text-gray-400 mt-0.5">
              Damy znać o nowych szczeniakach w obserwowanych hodowlach.
            </p>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button
              size="sm" variant="outline"
              onClick={handleTest} disabled={busy}
              className="rounded-full border-white/15 bg-transparent text-white hover:bg-white/5 text-xs h-8"
              data-testid="push-toggle-test"
            >
              Test
            </Button>
            <Button
              size="sm" variant="outline"
              onClick={handleDisable} disabled={busy}
              className="rounded-full border-white/15 bg-transparent text-gray-300 hover:bg-white/5 text-xs h-8"
              data-testid="push-toggle-off-btn"
            >
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Wyłącz'}
            </Button>
          </div>
        </div>
      );
    }

    // default — pokaz CTA
    return (
      <div
        className="rounded-xl bg-gradient-to-br from-indigo-500/10 to-rose-500/10 border border-white/10 p-4 flex flex-wrap items-center gap-3"
        data-testid="push-toggle-cta"
      >
        <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center shrink-0">
          <BellRing className="w-4 h-4 text-white" />
        </div>
        <div className="flex-1 min-w-[180px]">
          <p className="text-sm font-semibold text-white">Daj znać o nowych szczeniakach</p>
          <p className="text-xs text-gray-400 mt-0.5">
            Bezpłatne powiadomienia w przeglądarce — bez maila, bez konta.
          </p>
        </div>
        <Button
          size="sm" onClick={handleEnable} disabled={busy}
          className="shrink-0 rounded-full bg-white text-black hover:bg-white/90 font-semibold text-xs h-9 px-4 ml-auto"
          data-testid="push-toggle-on-btn"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Bell className="w-3.5 h-3.5 mr-1.5" />Włącz</>}
        </Button>
      </div>
    );
  }

  // ============== VARIANT: inline (compact dla TopNavBar) ==============
  if (state.permission === 'denied' || state.subscribed) return null;
  return (
    <Button
      size="sm" onClick={handleEnable} disabled={busy}
      className="rounded-full bg-white/10 hover:bg-white/15 text-white text-xs h-8 px-3"
      data-testid="push-toggle-inline-btn"
    >
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Bell className="w-3.5 h-3.5 mr-1" />Powiadom mnie</>}
    </Button>
  );
}
